import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Button, Flex, useMediaQuery } from '@chakra-ui/react';
import Link from 'next/link';
import React, { useState } from 'react';
import Menu from '../../../public/icons/menu.svg';

import {
    Drawer,
    DrawerBody,
    DrawerCloseButton,
    DrawerContent,
    DrawerFooter,
    DrawerHeader,
    DrawerOverlay,
} from '@chakra-ui/react';
import { colors } from '../../../constants/constants';
import { useRouter } from 'next/router';
import { MainMenu, MainMenuItem } from '../../../types/umbraco';
import Logout from '../logout/logout';
import MyPages from '../mypages/mypages';

const SafeButton = Button as React.ComponentType<any>;

export default function MobileNavigation({
  menu,
}: {
  menu: MainMenu;
}): JSX.Element {
  const [showDrawer, setShowDrawer] = useState(false);
  const [isDesktop] = useMediaQuery('(min-width: 48em)'); // 48em is what md sets.
  const router = useRouter();

  const toggleMenu = () => {
    setShowDrawer(!showDrawer);
  };

  React.useEffect(() => {
    if (isDesktop) {
      setShowDrawer(false);
    }
  }, [isDesktop]);

  React.useEffect(() => {
    const closeMenu = () => setShowDrawer(false);
    router.events.on('routeChangeComplete', closeMenu);

    return () => router.events.off('routeChangeComplete', closeMenu);
  }, [router.events]);

  return (
    <Flex
      w="100%"
      d={{ base: 'flex', sm: 'flex', md: 'none', lg: 'none', xl: 'none' }}
      alignItems="center"
      justifyContent="flex-end">
      <Box m="0" p="0" lineHeight="0">
        <SafeButton
          type="button"
          aria-label={showDrawer ? 'Stäng meny' : 'Öppna meny'}
          aria-expanded={showDrawer}
          aria-controls="mobile-navigation-drawer"
          onClick={toggleMenu}
          minW="44px"
          minH="44px"
          p="2"
          bg={colors.primary[900]}
          color={colors.white}
          _hover={{ bg: colors.secondary[900] }}
          _focusVisible={{
            boxShadow: `0 0 0 3px ${colors.secondary[900]}`,
          }}>
          <Menu aria-hidden="true" focusable="false" width="2em" height="2em" />
        </SafeButton>

        <Drawer
          isOpen={showDrawer}
          placement="left"
          onClose={toggleMenu}>
          <DrawerOverlay />
          <DrawerContent
            id="mobile-navigation-drawer"
            bg={colors.white}
            color={colors.neutrals[900]}>
            <DrawerCloseButton />
            <DrawerHeader>Meny</DrawerHeader>

            <DrawerBody>
              <MyPages style={{ marginBottom: 20 }} />
              <Flex
                as="nav"
                aria-label="Huvudnavigation"
                justifyContent="space-around"
                flex="1"
                fontSize="16px"
                fontWeight={600}
                flexDir="column"
                lineHeight="2em">
                {menu?.items?.map((x, i) => (
                  <DropDownMenu key={i} {...x} />
                ))}
              </Flex>
            </DrawerBody>

            <DrawerFooter>
              <Logout />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </Box>
    </Flex>
  );
}

const mobileMenuFontSize = '16px';
function DropDownMenu(x: MainMenuItem): JSX.Element {
  const [show, setShow] = useState(false);

  if (!x.children) {
    return (
      <Link href={x.path} style={{ fontSize: mobileMenuFontSize }}>
        {x.name}
      </Link>
    );
  }

  const submenuId = `mobile-submenu-${x.path.replace(/[^a-zA-Z0-9]/g, '-')}`;

  return (
    <>
      <Flex alignItems="center">
        <Link href={x.path} style={{ fontSize: mobileMenuFontSize }}>
          {x.name}
        </Link>
        <Box
          as="button"
          type="button"
          aria-label={`Visa undersidor för ${x.name}`}
          aria-haspopup="true"
          aria-expanded={show}
          aria-controls={submenuId}
          ml={1}
          lineHeight="1"
          onClick={() => setShow(!show)}
        >
          {show ? <ChevronUpIcon /> : <ChevronDownIcon />}
        </Box>
      </Flex>
      <Flex
        id={submenuId}
        flexDir="column"
        mb="1em"
        px="1em"
        display={show ? 'flex' : 'none'}
        borderLeftWidth={1}
        borderLeftColor={colors.neutrals[300]}>
        {x.children?.map((y, i) => (
          <Link
            href={y.path}
            key={i}
            style={{ fontSize: mobileMenuFontSize }}>
            {y.name}
          </Link>
        ))}
      </Flex>
    </>
  );
}
